import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import LastPageIcon from '@mui/icons-material/LastPage';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import { useReportTableListDetails } from '../../data/DataProviders/DeviceReportTableDataProvider';
import { isDataLoaded } from '../../types';
import { DataIndicators } from '../DataIndicators';
import { FilePdf } from '@brightlayer-ui/icons-mui';
import { Icon } from '../../lib/icons';
import { EnhancedTableHead } from '../Table/EnhancedTableHead';
import { stableSort, getComparator } from '../../lib/table-helpers';
import makeStyle from '@mui/styles/makeStyles';

const useStyles = makeStyle({
    downloadIcon: {
        color: '#727E84',
        marginLeft: '20px',
    },
    pdfColorCode: { color: '#727E84' },

    pdfIcon: {
        position: 'relative',
        top: '-5px',
        left: '15px',
    },

    pagination: { float: 'right', marginRight: '7%' },
});

type Data = {
    Name: string;
    idIndex: string;
    Download: string;
};

const convertUserList = (userList: any): any => {
    const loadedUserList: any = userList.reportData && userList.reportData.length > 0 ? userList.reportData : [];
    const userlist: any = [];
    loadedUserList.forEach((user: any, index: any) => {
        const alarmDate = new Date(user.monthAndYear);
        alarmDate.setMonth(alarmDate.getMonth());
        const alarmDateArr = alarmDate.toLocaleDateString().split('/');
        let month = alarmDateArr[0];
        if (alarmDateArr[0].length < 2) month = `0${alarmDateArr[0]}`;
        const newAlarmEndDate = `${alarmDateArr[2].substring(2, 4)}${month}`;

        const obj = {
            idIndex: index,
            deviceId: user.deviceId ? user.deviceId : '',
            Name: user.deviceName ? user.deviceName : '',
            downloadUrl: user.downloadUrl,
            Date: user.monthAndYear ? user.monthAndYear : '',
            titleDate: newAlarmEndDate,
        };
        userlist.push(obj);
    });
    return userlist;
};
type Order = 'asc' | 'desc';

type HeadCell = {
    disablePadding: boolean;
    id: keyof Data;
    label: string;
    numeric: boolean;
    hideSorting?: boolean;
};

const headCells: HeadCell[] = [
    {
        id: 'idIndex',
        numeric: false,
        disablePadding: false,
        label: 'Name',
        hideSorting: false,
    },
    {
        id: 'idIndex',
        numeric: false,
        disablePadding: false,
        label: 'Date',
        hideSorting: false,
    },
    {
        id: 'Download',
        numeric: false,
        disablePadding: false,
        label: 'Download',
        hideSorting: true,
    },
];

type TablePaginationActionsProps = {
    count: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
};
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const TablePaginationActions = (props: TablePaginationActionsProps) => {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, 0);
        return true;
    };

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page - 1);
    };
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page + 1);
    };
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
                {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton>
            <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton>
        </Box>
    );
};

export const ReportGeneratePdf: React.FC = () => {
    const useStyle = useStyles();
    const reportListTableData = useReportTableListDetails();
    const getReportListData = isDataLoaded(reportListTableData) ? reportListTableData.value : [];
    const isEmpty = isDataLoaded(reportListTableData) ? reportListTableData.value.reportData.length === 0 : false;
    const formatedUserList = convertUserList(getReportListData);
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof Data>('idIndex');
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);       
    };

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);        
    };
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {       
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
   
    return (
        <>
            <Paper>
                <DataIndicators
                    errorMessage="Record not found"
                    emptyMessage="Record not found"
                    data={reportListTableData}
                    emptyIcon="BarChart"
                    isEmpty={isEmpty}
                    isDetailedInfo={false}
                />
                {isDataLoaded(reportListTableData) && reportListTableData.value.reportData.length > 0 && (
                    <>
                        <TableContainer>
                            
                        <Table sx={{ minWidth: 500 }} data-testid="display-table-list" aria-label="table" size={'medium'}>
                                    <EnhancedTableHead
                                        order={order}
                                        orderBy={orderBy}
                                        onRequestSort={handleRequestSort}
                                        headCells={headCells}
                                    />
                                    <TableBody data-testid="table-row">
                                        {stableSort(formatedUserList, getComparator(order, orderBy))
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((row: any, index: any) => (
                                                <TableRow hover key={index}>
                                                    <TableCell align="left">{''}</TableCell>
                                                    <TableCell align="left" style={{ width: '53%' }}>
                                                        <FilePdf className={useStyle.pdfColorCode} />
                                                        <span className={useStyle.pdfIcon}>
                                                            {' '}
                                                            {row.Name} {row.titleDate}{' '}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell style={{ width: '33%' }} align="left">
                                                        {row.Date}
                                                    </TableCell>
                                                    <TableCell style={{ width: '33%' }} align="left">
                                                        <a href={row.downloadUrl}>
                                                            <Icon.GetApp
                                                                className={useStyle.downloadIcon}
                                                                titleAccess={row.downloadUrl}
                                                            />
                                                        </a>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>

                            <div className={useStyle.pagination}>
                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25,100]}
                                    colSpan={3}
                                    count={formatedUserList.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    SelectProps={{
                                        inputProps: {
                                            'aria-label': 'rows per page',
                                        },
                                        native: true,
                                    }}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    ActionsComponent={TablePaginationActions}
                                />
                            </div>
                        </TableContainer>
                    </>
                )}
            </Paper>
        </>
    );
};